import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateInvite, acceptInvite } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import { useTimezone } from '../context/TimezoneContext';

export default function AcceptInvite() {
  const isMobile = useIsMobile(520);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();
  const { formatTime } = useTimezone();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invite, setInvite] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link.');
      setLoading(false);
      return;
    }
    validateInvite(token)
      .then((data) => {
        setInvite(data);
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'This invitation is invalid or has expired.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setAccepting(true);
    setError('');
    try {
      const data = await acceptInvite(token, password);
      login(data.user);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  }

  async function handleJoin() {
    if (!token) return;
    setAccepting(true);
    setError('');
    try {
      const data = await acceptInvite(token);
      login(data.user);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  }

  const pageStyle: React.CSSProperties = {
    ...styles.page,
    padding: isMobile ? '1rem' : styles.page.padding,
  };
  const cardStyle: React.CSSProperties = {
    ...styles.card,
    maxWidth: 'min(420px, calc(100vw - 24px))',
    padding: isMobile ? '1.25rem' : styles.card.padding,
  };
  const detailRowStyle: React.CSSProperties = {
    ...styles.detailRow,
    alignItems: isMobile ? 'flex-start' : styles.detailRow.alignItems,
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '0.25rem' : undefined,
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle} className="glass-card">
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📩</div>
          <h2 style={styles.title}>Accept Invitation</h2>
          <p style={styles.subtitle}>Join your team on Seedronics</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {loading ? (
          <p style={styles.hint}>Verifying invitation…</p>
        ) : invite ? (
          <>
            <div style={styles.details}>
              <div style={detailRowStyle}>
                <span style={styles.detailLabel}>Tenant</span>
                <span style={styles.detailValue}>{invite.tenant_name}</span>
              </div>
              <div style={detailRowStyle}>
                <span style={styles.detailLabel}>Email</span>
                <span style={styles.detailValue}>{invite.email}</span>
              </div>
              <div style={detailRowStyle}>
                <span style={styles.detailLabel}>Role</span>
                <span style={styles.roleBadge}>{invite.role}</span>
              </div>
              <div style={detailRowStyle}>
                <span style={styles.detailLabel}>Expires</span>
                <span style={styles.detailValue}>{formatTime(invite.expires_at)}</span>
              </div>
            </div>

            {invite.account_exists ? (
              <>
                <p style={styles.info}>
                  This email already has a Seedronics account. Join {invite.tenant_name} with the existing login.
                </p>
                <button
                  disabled={accepting}
                  onClick={handleJoin}
                  style={{ ...styles.btn, opacity: accepting ? 0.5 : 1 }}
                  type="button"
                >
                  {accepting ? 'Joining…' : 'Join workspace'}
                </button>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={styles.field}>
                  <label style={styles.label}>Password</label>
                  <input
                    style={styles.input}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    minLength={6}
                  />
                  <small style={styles.helpText}>Create a password for this invited workspace account.</small>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Confirm Password</label>
                  <input
                    style={styles.input}
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    minLength={6}
                  />
                  <small style={styles.helpText}>Must match the password above before joining.</small>
                </div>
                <button
                  type="submit"
                  style={{ ...styles.btn, opacity: accepting ? 0.5 : 1 }}
                  disabled={accepting}
                >
                  {accepting ? 'Accepting…' : 'Accept Invitation'}
                </button>
              </form>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    background: 'var(--bg-deep)',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    padding: '2rem',
    color: 'var(--text-primary)',
  },
  title: {
    margin: '0 0 0.25rem 0',
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  subtitle: {
    margin: 0,
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
  },
  details: {
    padding: '1rem',
    borderRadius: '10px',
    background: 'rgba(22, 25, 34, 0.5)',
    border: '1px solid rgba(255,255,255,0.04)',
    marginBottom: '1.5rem',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },
  detailLabel: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  detailValue: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  roleBadge: {
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    background: 'rgba(45,212,168,0.1)',
    color: '#2DD4A8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: 'var(--text-secondary)',
  },
  input: {
    width: '100%',
    padding: '0.625rem 0.875rem',
    borderRadius: '10px',
    background: 'rgba(22, 25, 34, 0.8)',
    border: '1px solid rgba(255,255,255,0.06)',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  helpText: {
    display: 'block',
    marginTop: '0.4rem',
    color: 'var(--text-muted)',
    fontSize: '0.72rem',
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  btn: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '10px',
    border: 'none',
    background: '#2DD4A8',
    color: '#0B0D14',
    fontSize: '0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
  },
  error: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    background: 'rgba(232, 96, 44, 0.12)',
    border: '1px solid rgba(232, 96, 44, 0.25)',
    color: '#e8602c',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  info: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    background: 'rgba(45, 212, 168, 0.08)',
    border: '1px solid rgba(45, 212, 168, 0.2)',
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
    lineHeight: 1.45,
    marginBottom: '1rem',
  },
  hint: {
    margin: 0,
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    textAlign: 'center',
  },
};
